/* Onboarding Agent — agentic ingestion for any unknown bundle.
 *
 * Two stages, both pure functions:
 *   1. discoverSchema(bundle)  — auto-detects the shape of what's
 *                                been handed to us. Counts top-level
 *                                maps, identifies the "type" key by
 *                                frequency, clusters objects by
 *                                that key, picks a `name` field
 *                                heuristically (the most common
 *                                string field across entries). No
 *                                hardcoded schema — the agent finds
 *                                it from the data.
 *   2. mapToOntology(bundle, schema, meta) — walks the discovered
 *                                shape and emits typed FoyrMind
 *                                graph nodes + edges, with a
 *                                reasoning trace explaining *why*
 *                                each entity got its kind.
 *
 * Caller calls runOnboardingAgent(bundle, meta) — gets back a typed
 * ProjectGraph plus the reasoning trace the cockpit can render. */

import type { GraphNode, GraphEdge, ProjectGraph, NodeKind } from "../types/ontology";
import rulesData from "../data/onboarding-mapping-rules.json";

interface MappingRule { match_any: string[]; kind?: string; why: string }
interface MappingRules {
  rules: MappingRule[];
  skip_as_typed_node: MappingRule[];
  carry_as_project_attrs: string[];
}
const RULES = rulesData as MappingRules;

export interface SchemaProbe {
  top_level_maps: { key: string; entries: number; sample_keys: string[] }[];
  inferred_type_field: string | null;
  inferred_name_field: string | null;
  type_clusters: { type_value: string; count: number; inferred_kind: NodeKind | null; rationale: string }[];
  total_entities_seen: number;
}

export interface ReasoningStep {
  kind: "think" | "act" | "observe";
  text: string;
}

export interface OnboardingResult {
  graph: ProjectGraph;
  schema: SchemaProbe;
  trace: ReasoningStep[];
  stats: { nodes: number; edges: number; skus: number; materials: number; zones: number; rooms: number; alerts: number };
}

export interface ProjectMetadata {
  id: string;
  name: string;
  client?: string;
  city?: string;
  budgetINR?: number;
  deadline?: string; // ISO date
}

/** Phase 1 — discoverSchema: examine the bundle, return a probe. */
export function discoverSchema(bundle: Record<string, unknown>): SchemaProbe {
  const topLevel: SchemaProbe["top_level_maps"] = [];
  const typeFreq = new Map<string, number>();
  const fieldFreq = new Map<string, number>();
  let totalEntities = 0;

  for (const [k, v] of Object.entries(bundle)) {
    if (!v || typeof v !== "object") continue;
    const entries = Array.isArray(v) ? v.length : Object.keys(v as Record<string, unknown>).length;
    const sampleKeys = (Array.isArray(v) ? v.slice(0, 3).map((_, i) => `${i}`) : Object.keys(v as Record<string, unknown>).slice(0, 3));
    topLevel.push({ key: k, entries, sample_keys: sampleKeys });

    // Walk one level into entries to probe fields.
    const iter = Array.isArray(v) ? v : Object.values(v as Record<string, unknown>);
    for (const entry of iter) {
      if (!entry || typeof entry !== "object") continue;
      totalEntities++;
      for (const [fk, fv] of Object.entries(entry as Record<string, unknown>)) {
        fieldFreq.set(fk, (fieldFreq.get(fk) ?? 0) + 1);
        if (typeof fv === "string" && (fk === "type" || fk === "kind")) {
          typeFreq.set(fv, (typeFreq.get(fv) ?? 0) + 1);
        }
      }
    }
  }

  // The "type" field is the string field most often present that
  // also has bounded cardinality (few distinct values).
  const candidateTypeFields = ["type", "kind"].filter((f) => fieldFreq.has(f));
  const typeField = candidateTypeFields.length > 0 ? candidateTypeFields[0]! : null;
  // The "name" field is the most common string-valued label-shaped
  // field; we prefer `name` if present, fall back to `label`/`title`.
  const nameField = ["name", "label", "title", "display_name"].find((f) => fieldFreq.has(f)) ?? null;

  // Cluster by type value, infer FoyrMind kind from each cluster.
  const clusters: SchemaProbe["type_clusters"] = [];
  for (const [typeValue, count] of typeFreq.entries()) {
    const inferred = inferKindFromTypeValue(typeValue);
    clusters.push({
      type_value: typeValue,
      count,
      inferred_kind: inferred.kind,
      rationale: inferred.rationale,
    });
  }
  clusters.sort((a, b) => b.count - a.count);

  return {
    top_level_maps: topLevel.sort((a, b) => b.entries - a.entries),
    inferred_type_field: typeField,
    inferred_name_field: nameField,
    type_clusters: clusters,
    total_entities_seen: totalEntities,
  };
}

/** Classifier driven by `data/onboarding-mapping-rules.json` —
 *  ontology-aware, zero hardcoded NodeKind literals in this file.
 *  Add or reorder rules in the JSON to change behaviour. */
function inferKindFromTypeValue(typeValue: string): { kind: NodeKind | null; rationale: string } {
  const t = typeValue.toLowerCase();
  for (const rule of RULES.skip_as_typed_node) {
    if (rule.match_any.some((p) => t.includes(p.toLowerCase()))) {
      return { kind: null, rationale: `'${typeValue}' → skipped: ${rule.why}` };
    }
  }
  for (const rule of RULES.rules) {
    if (rule.match_any.some((p) => t.includes(p.toLowerCase()))) {
      return { kind: (rule.kind as NodeKind) ?? null, rationale: `'${typeValue}' → ${rule.kind} · ${rule.why}` };
    }
  }
  return { kind: null, rationale: `'${typeValue}' unrecognised — left untyped (designer-confirm)` };
}

/** Phase 2 — mapToOntology: emit typed graph + reasoning trace. */
function mapToOntology(
  bundle: Record<string, unknown>,
  schema: SchemaProbe,
  meta: ProjectMetadata,
  trace: ReasoningStep[],
): { graph: ProjectGraph; stats: OnboardingResult["stats"] } {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const seen = new Set<string>();
  let edgeCounter = 0;
  const stats = { nodes: 0, edges: 0, skus: 0, materials: 0, zones: 0, rooms: 0, alerts: 0 };

  const projectNode: GraphNode = {
    id: meta.id,
    kind: "Project",
    label: meta.name,
    attrs: { source: "onboarding-agent", capturedFrom: "auto-discovered" },
  };
  nodes.push(projectNode); seen.add(projectNode.id); stats.nodes++;

  function addEdge(kind: GraphEdge["kind"], source: string, target: string): void {
    edges.push({
      id: `e-${++edgeCounter}-${source.slice(0, 8)}-${target.slice(0, 8)}`,
      kind, source, target, weight: 1, attrs: {},
    });
    stats.edges++;
  }

  const typeField = schema.inferred_type_field ?? "type";
  const nameField = schema.inferred_name_field ?? "name";

  // Map cluster type_value → inferred_kind for O(1) lookup.
  const kindByType = new Map(schema.type_clusters.map((c) => [c.type_value.toLowerCase(), c.inferred_kind]));

  function walk(raw: unknown, parentId: string, depth: number): string | null {
    if (raw === null || raw === undefined || typeof raw !== "object" || depth > 6) return null;
    if (Array.isArray(raw)) {
      for (const c of raw) walk(c, parentId, depth + 1);
      return null;
    }
    const o = raw as Record<string, unknown>;
    const uuidVal = o["uuid"];
    const idVal = o["id"];
    const uuid: string | null =
      typeof uuidVal === "string" ? uuidVal :
      typeof idVal === "string" ? idVal : null;
    const tv = o[typeField];
    const rawType: string | null = typeof tv === "string" ? tv : null;
    if (!uuid || !rawType) {
      if (Array.isArray(o["children"])) for (const c of o["children"] as unknown[]) walk(c, parentId, depth + 1);
      return null;
    }
    if (seen.has(uuid)) { addEdge("CONTAINS", parentId, uuid); return uuid; }
    const kind = kindByType.get(rawType.toLowerCase()) ?? null;
    if (!kind) {
      if (Array.isArray(o["children"])) for (const c of o["children"] as unknown[]) walk(c, parentId, depth + 1);
      return null;
    }
    const nv = o[nameField];
    const label: string = typeof nv === "string" && nv ? nv : uuid.slice(0, 8);
    nodes.push({
      id: uuid,
      kind,
      label,
      attrs: { foyr_type: rawType, source: "onboarding-agent" },
    });
    seen.add(uuid); stats.nodes++;
    if (kind === "SKU") stats.skus++;
    if (kind === "Material") stats.materials++;
    if (kind === "Zone") stats.zones++;
    if (kind === "Room") stats.rooms++;
    // Edge kind comes from the ontology — IN_ROOM for SKU placement,
    // CONTAINS for every other parent-child relationship.
    const edgeKind: GraphEdge["kind"] = kind === ("SKU" as NodeKind) ? "IN_ROOM" : "CONTAINS";
    addEdge(edgeKind, parentId, uuid);
    if (Array.isArray(o["children"])) {
      for (const c of o["children"] as unknown[]) walk(c, uuid, depth + 1);
    }
    return uuid;
  }

  for (const [mapKey, mapVal] of Object.entries(bundle)) {
    if (!mapVal || typeof mapVal !== "object") continue;
    const carryAsAttr = RULES.carry_as_project_attrs.some((p) => mapKey.toLowerCase().includes(p));
    if (carryAsAttr) {
      const projectAttrs = projectNode.attrs as Record<string, unknown>;
      projectAttrs[mapKey] = mapVal;
      trace.push({ kind: "act", text: `Carried '${mapKey}' onto Project as attribute (rule: carry_as_project_attrs).` });
      continue;
    }
    const iter = Array.isArray(mapVal) ? mapVal : Object.values(mapVal as Record<string, unknown>);
    for (const e of iter) walk(e, projectNode.id, 0);
  }

  if (stats.skus === 0 && stats.rooms === 0) {
    const alertId = `${projectNode.id}-al-empty`;
    nodes.push({
      id: alertId,
      kind: "Alert" as NodeKind,
      label: "Empty starter scene — drop your first room",
      attrs: { severity: "info", source: "onboarding-agent" },
    });
    addEdge("OF", alertId, projectNode.id);
    stats.nodes++; stats.alerts++;
  }

  // ── Carry the designer-entered metadata onto the Project node
  //    as typed attributes — NO synthesis, NO heuristics. The
  //    designer typed these values; we record them, the agents
  //    reason over them. If a value is absent, the typed graph
  //    stays honest about what it doesn't know.
  const projectAttrs = projectNode.attrs as Record<string, unknown>;
  if (meta.client)   projectAttrs["client"]   = meta.client;
  if (meta.city)     projectAttrs["city"]     = meta.city;
  if (meta.deadline) projectAttrs["deadline"] = meta.deadline;
  if (meta.budgetINR && meta.budgetINR > 0) {
    projectAttrs["budgetCap"] = meta.budgetINR;
    projectAttrs["currency"]  = "INR";
  }

  return {
    graph: { projectId: projectNode.id, projectName: meta.name, nodes, edges },
    stats,
  };
}

/** runOnboardingAgent — orchestrate discover → map. Returns the
 *  typed graph + a designer-readable reasoning trace. */
export function runOnboardingAgent(
  bundle: Record<string, unknown>,
  meta: ProjectMetadata,
): OnboardingResult {
  const trace: ReasoningStep[] = [];
  trace.push({ kind: "think", text: `Received bundle with ${Object.keys(bundle).length} top-level maps. I don't know the schema — discovering it.` });
  const schema = discoverSchema(bundle);
  trace.push({ kind: "observe", text: `${schema.top_level_maps.length} maps · ${schema.total_entities_seen} total entries.` });
  if (schema.inferred_type_field) trace.push({ kind: "observe", text: `Field '${schema.inferred_type_field}' is the type discriminator.` });
  if (schema.inferred_name_field) trace.push({ kind: "observe", text: `Field '${schema.inferred_name_field}' is the label.` });
  for (const c of schema.type_clusters.slice(0, 8)) {
    trace.push({ kind: c.inferred_kind ? "act" : "think", text: `${c.count}× '${c.type_value}' → ${c.inferred_kind ?? "untyped"} · ${c.rationale}` });
  }
  trace.push({ kind: "act", text: `Mapping into typed graph with ${meta.name} as the Project root.` });
  const { graph, stats } = mapToOntology(bundle, schema, meta, trace);
  trace.push({ kind: "observe", text: `Emitted ${stats.nodes} typed nodes · ${stats.edges} edges (${stats.skus} SKU · ${stats.materials} Material · ${stats.zones} Zone · ${stats.rooms} Room · ${stats.alerts} Alert).` });
  return { graph, schema, trace, stats };
}
